import React, { useMemo } from "react";
import { Typography, InputNumber } from "antd";
import { SbTag, SbTooltip } from "uiCore";
import { getFilters } from "../RedeploymentCenterV2/RedeploymentCenterV2.utils";

const { Text } = Typography;

// Demand-step column builder — same split as useRedeploymentCenterColumns:
// the page owns state and handlers, this hook owns presentation. Handlers
// passed in must be referentially stable (useCallback in the page) since the
// memoized columns close over them.
export const useWorkforcePlanningColumns = ({ roles, onTargetChange, onTargetBlur }) =>
  useMemo(
    () => [
      {
        title: "Role",
        dataIndex: "title",
        filters: getFilters(roles, "title"),
        filterSearch: true,
        onFilter: (v, r) => r.title === v,
      },
      {
        title: "Function",
        dataIndex: "function",
        filters: getFilters(roles, "function"),
        filterSearch: true,
        onFilter: (v, r) => r.function === v,
      },
      {
        title: "Priority",
        dataIndex: "priority",
        align: "center",
        filters: getFilters(roles, "priority"),
        filterSearch: true,
        onFilter: (v, r) => r.priority === v,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (priority) => <SbTag color={priority === "High" ? "warning" : "default"}>{priority}</SbTag>,
      },
      {
        title: "Current Positions",
        dataIndex: "currentPositionCount",
        align: "center",
        sorter: (a, b) => a.currentPositionCount - b.currentPositionCount,
        // align: "center" only centers the body cells — a sortable header's
        // title+icon lays out in antd's own flex box, untouched by align.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      },
      {
        title: (
          <SbTooltip content="Set above Current Positions to plan growth for this role.">
            Target Positions (Demand)
          </SbTooltip>
        ),
        dataIndex: "targetPositionCount",
        // Editable cell — filtering on it would drop the row mid-edit.
        filterable: false,
        align: "center",
        sorter: (a, b) => a.targetPositionCount - b.targetPositionCount,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        // Workforce Planning only models growth; a reduction is Redeployment
        // Center's workflow. Values at or below Current clear the demand and
        // warn on blur — all growth logic keys on target > current. No
        // min={current + 1} clamp, since antd's blur-clamp would silently
        // commit an attempted decrease as +1 growth.
        render: (value, record) => (
          <InputNumber
            size="small"
            min={1}
            value={value}
            onClick={(event) => event.stopPropagation()}
            onChange={(nextValue) => onTargetChange(record, nextValue)}
            onBlur={() => onTargetBlur(record)}
          />
        ),
      },
      {
        title: "Status",
        key: "demandStatus",
        align: "center",
        filters: [
          { text: "Growing", value: true },
          { text: "—", value: false },
        ],
        filterSearch: true,
        onFilter: (value, record) => (record.targetPositionCount > record.currentPositionCount) === value,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (_, record) =>
          record.targetPositionCount > record.currentPositionCount ? (
            <SbTag color="success">Growing</SbTag>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ],
    [roles, onTargetChange, onTargetBlur]
  );
